/* AuthContext — sesion del usuario en el frontend (TASK-005).

DECISIONES:
  - La sesion vive en una cookie HttpOnly que pone el servidor. El front NO ve
    ni guarda el token: nada de localStorage, que lo robaria cualquier XSS.
  - Por eso el estado de auth se descubre llamando a `/api/auth/me` al arrancar:
    si responde 200, hay sesion; si 401, no la hay.
  - Todas las peticiones van con `withCredentials: true`, o la cookie no viaja.

POR QUE HACE FALTA UN CONTEXTO Y NO UN FLAG SUELTO:
    Router, header, paginas y el cliente axios necesitan saber quien es el
    usuario (para ocultar "Administrador" a un viewer, para el logout, para
    saber que restaurantes puede ver). Un solo lugar evita que cada componente
    consulte `/me` por su cuenta.
*/
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { apiClient } from "../api/client";

export type Rol = "admin" | "gerente" | "viewer";

export interface UsuarioSesion {
  id: string;
  username: string;
  email: string;
  name: string | null;
  role: Rol;
  restaurants: string[]; // slugs visibles; vacio = todos (admin)
}

interface AuthCtx {
  /** null = todavia no se sabe (evita parpadear el login al recargar). */
  user: UsuarioSesion | null;
  cargando: boolean;
  /** true si ya se consulto /me al menos una vez. */
  listo: boolean;
  esAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Vuelve a consultar /me (tras cambiar de usuario o rol). */
  refrescar: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UsuarioSesion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [listo, setListo] = useState(false);

  const consultarMe = useCallback(async () => {
    try {
      const { data } = await apiClient.get<{ user: UsuarioSesion; restaurants: string[] }>(
        "/auth/me",
        { withCredentials: true }
      );
      // `/me` devuelve { user: {...}, restaurants: [slugs] }. Se aplana en un
      // solo objeto para que los componentes no tengan que conocer el sobre.
      setUser({ ...data.user, restaurants: data.restaurants ?? [] });
    } catch {
      // 401 (u otro error): no hay sesion. No se distingue el motivo a
      // proposito: al usuario le da igual si expiro o nunca existio.
      setUser(null);
    } finally {
      setCargando(false);
      setListo(true);
    }
  }, []);

  useEffect(() => {
    consultarMe();
  }, [consultarMe]);

  const login = useCallback(async (username: string, password: string) => {
    // El servidor setea la cookie HttpOnly en la respuesta; el front no la
    // manipula ni la guarda. La respuesta trae el mismo sobre que /me:
    // { user: {...}, restaurants: [slugs] }.
    const { data } = await apiClient.post<{ user: UsuarioSesion; restaurants: string[] }>(
      "/auth/login",
      { username, password },
      { withCredentials: true }
    );
    setUser({ ...data.user, restaurants: data.restaurants ?? [] });
    setListo(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout", {}, { withCredentials: true });
    } finally {
      // Aunque el servidor falle, el front cierra la sesion local: no se
      // deja al usuario "a medio salir".
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      cargando,
      listo,
      esAdmin: user?.role === "admin",
      login,
      logout,
      refrescar: consultarMe,
    }),
    [user, cargando, listo, login, logout, consultarMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
